/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Navigate, useParams } from 'react-router-dom';
import { useHeaderStates } from '../../Providers/HeaderStates';
import { useStartupState } from '../../Providers/StartupState';
import { Loading } from '../../components/Loading';
import { SocialMediaButtons } from '../../components/SocialMediaButtons';
import { Vitrine } from '../../components/Vitrine';
import { ROTULOS, aplicarMetadados, carregarGaleria, contagem, dataPorExtenso, semAcento } from './dados';
import { FotoEnquadrada } from './index';
import { Visualizador } from './Visualizador';
import * as S from './style';

// A grade mostra as fotos em blocos: há álbum com mais de mil fotos.
const POR_VEZ = 30;
const OUTROS = 12;

const useGaleria = () => {
  const [galeria, setGaleria] = useState(null);
  const [pronto, setPronto] = useState(false);
  const { setTitle, setBgHeader } = useHeaderStates();
  const { token, setError, setLoading } = useStartupState();

  useEffect(() => {
    setError(null);
    setLoading(true);
    if (!token) return;
    carregarGaleria(token)
      .then(setGaleria)
      .catch((e) => console.log(e.message))
      .finally(() => {
        setPronto(true);
        setLoading(false);
      });
  }, [token]);

  useEffect(() => {
    setTitle(null);
    setBgHeader(null);
  });

  return { galeria, pronto };
};

const Compartilhar = ({ rotulo, titulo }) => {
  const [aberto, setAberto] = useState(false);
  if (!rotulo) return null;
  const url = window.location.href;

  // No celular abre o menu de compartilhar do próprio aparelho; sem ele, os
  // botões de rede social que o resto do site já usa.
  const compartilhar = () => {
    if (navigator.share) {
      navigator.share({ title: titulo, url }).catch(() => {});
    } else {
      setAberto(!aberto);
    }
  };

  return (
    <S.Compartilhar>
      <button type="button" onClick={compartilhar} aria-expanded={navigator.share ? undefined : aberto}>
        {rotulo} <ArrowUpRight size={14} aria-hidden="true" />
      </button>
      {aberto && <SocialMediaButtons url={url} text={titulo} open={() => setAberto(false)} />}
    </S.Compartilhar>
  );
};

// Cabeçalho e setas do "Fique por dentro" da Home; os cartões são os de foto.
const OutrosAlbuns = ({ albuns, titulo }) => {
  if (!albuns.length) return null;
  return (
    <S.Outros>
      <S.Container>
        <Vitrine
          chapeu={ROTULOS.outrosChapeu}
          titulo={titulo}
          rotuloAnterior="Álbuns anteriores"
          rotuloProximo="Próximos álbuns"
        >
          {albuns.map((album) => (
            <S.CartaoOutro key={album.id} to={`/galeriaDeFotos/${album.endereco}`}>
              <FotoEnquadrada id={album.capa} largura={720} alt="" />
              <div>
                <span>
                  {dataPorExtenso(album.data)} · {contagem(album.fotos.length, ROTULOS.foto)}
                </span>
                <h3>{album.titulo}</h3>
              </div>
            </S.CartaoOutro>
          ))}
        </Vitrine>
      </S.Container>
    </S.Outros>
  );
};

export const Album = () => {
  const { endereco } = useParams();
  const { galeria, pronto } = useGaleria();
  const [quantos, setQuantos] = useState(POR_VEZ);
  const [aberta, setAberta] = useState(null);

  const album = galeria?.lista.find((a) => a.endereco === endereco);

  useEffect(() => {
    setQuantos(POR_VEZ);
    setAberta(null);
    window.scrollTo({ top: 0 });
  }, [endereco]);

  // Cada álbum usa o próprio título e descrição no Google.
  useEffect(() => {
    if (!album) return undefined;
    const sufixo = galeria.metadados.titulo;
    return aplicarMetadados({
      titulo: sufixo ? `${album.titulo} · ${sufixo}` : album.titulo,
      descricao: album.descricao,
    });
  }, [album]);

  if (!pronto) return <Loading />;
  if (!galeria) return null;
  if (!album) return <Navigate to="/galeriaDeFotos" replace />;

  const textos = galeria.album;
  const visiveis = album.fotos.slice(0, quantos);
  const outros = galeria.lista.filter((a) => a.id !== album.id).slice(0, OUTROS);

  return (
    <S.Pagina>
      <S.Container>
        <S.CabecalhoAlbum>
          {/* o álbum não está no menu: o caminho é o da galeria, mais o título */}
          <S.TrilhaDaPagina rota="/galeriaDeFotos" atual={album.titulo} />
          <S.Chapeu as="time" dateTime={album.data.slice(0, 10)}>
            {dataPorExtenso(album.data)}
          </S.Chapeu>
          <S.Titulo>{album.titulo}</S.Titulo>
          {album.descricao && <p>{album.descricao}</p>}
          <Compartilhar rotulo={ROTULOS.compartilhar} titulo={album.titulo} />
        </S.CabecalhoAlbum>

        <S.TopoDaGrade>
          <strong>{textos.titulo}</strong>
          <span>
            {contagem(album.fotos.length, ROTULOS.foto)}
            {` · ${ROTULOS.dica}`}
          </span>
        </S.TopoDaGrade>

        <S.Fotos>
          {visiveis.map((id, i) => (
            <button
              type="button"
              key={`${id}-${i}`}
              onClick={() => setAberta(i)}
              aria-label={`${album.titulo} — ${i + 1} / ${album.fotos.length}`}
            >
              <FotoEnquadrada id={id} largura={i % 11 === 0 ? 900 : 480} alt="" />
              <S.Numero aria-hidden="true">{String(i + 1).padStart(2, '0')}</S.Numero>
            </button>
          ))}
        </S.Fotos>

        {album.fotos.length > quantos && (
          <S.CarregarMais type="button" onClick={() => setQuantos(quantos + POR_VEZ)}>
            {ROTULOS.carregarMais}
          </S.CarregarMais>
        )}
        {album.fotos.length <= quantos && <S.Fim $noMeio>{ROTULOS.fim}</S.Fim>}
      </S.Container>

      <OutrosAlbuns albuns={outros} titulo={textos.outrosTitulo} />

      {aberta !== null && (
        <Visualizador
          titulo={album.titulo}
          fotos={album.fotos}
          inicio={aberta}
          ajuda={ROTULOS.ajuda}
          aoFechar={(ultima) => {
            setAberta(null);
            // a foto vista por último fica carregada na grade, para o foco voltar a ela
            if (ultima >= quantos) setQuantos(Math.ceil((ultima + 1) / POR_VEZ) * POR_VEZ);
          }}
        />
      )}
    </S.Pagina>
  );
};

// Endereço do modelo antigo: /fotos/<idUrl>, com acento e maiúscula. O idUrl
// não existe no modelo novo, então o álbum é achado pelo endereço parecido
// (sem acento, minúsculo, contido no endereço novo inteiro por palavras:
// "Giro-do-Milho-Copasul-2026" acha "13-giro-do-milho-copasul-2026"). Sem
// correspondência, vai para a galeria.
export const AlbumAntigo = () => {
  const { idPhoto = '' } = useParams();
  const { galeria, pronto } = useGaleria();

  if (!pronto) return <Loading />;
  const procurado = semAcento(decodeURIComponent(idPhoto))
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const album =
    procurado && galeria?.lista.find((a) => `-${a.endereco}-`.includes(`-${procurado}-`));
  return <Navigate to={album ? `/galeriaDeFotos/${album.endereco}` : '/galeriaDeFotos'} replace />;
};
